import { BalanceData } from "../data/balance";
import { PET_ABILITY_SLOTS, PetAbilitySlot, PetHitData } from "../data/petAbilityData";
import { PetData } from "../data/pets";
import { Fix64 } from "../maths/fix64";
import { SimConstants } from "../sim/constants";
import { PetHit, StrikeTarget } from "../sim/petKit";
import type { PetRule } from "../sim/petRule";

/**
 * A pet's abilities at a level (Pets v2/v3, docs/PETS.md): each slot unlocks at its `fromLevel`; damage (an attack's, a
 * pulse's or a signature's, and a burn's per-second damage) follows the towers' level curve (tables.levelMult: ×1.07 a
 * level, the precomputed table, so no transcendental function runs); every other number is flat. `petRule` is the run's
 * copy the sim reads (LevelModifiers.pet): only the abilities unlocked at that level are in it. The pup's kinds go in
 * its v2 fields (`bite`, `spawnCutPercent`, `bark`, `howl`), the others in the Pets v3 ones (sim/petKit.ts).
 */

/** Whether `slot` is unlocked at `level`. */
export function abilityUnlocked(pet: PetData, slot: PetAbilitySlot, level: number): boolean { return level >= pet.abilities[slot].fromLevel; }

/** The abilities still locked at `level`, each with the level it unlocks at, in the sheet's order. */
export function lockedAbilities(pet: PetData, level: number): { readonly id: PetAbilitySlot; readonly fromLevel: number }[] {
  return PET_ABILITY_SLOTS.filter((a) => !abilityUnlocked(pet, a, level)).map((id) => ({ id, fromLevel: pet.abilities[id].fromLevel }));
}

/** `level` clamped to 1 … the towers' max level. */
export function petLevelClamp(balance: BalanceData, level: number): number { return Math.max(1, Math.min(balance.tables.maxLevel, Math.floor(level))); }

const PERCENT = 100;
const TARGETS: Record<string, StrikeTarget> = { ground: StrikeTarget.Ground, any: StrikeTarget.Any, airFirst: StrikeTarget.AirFirst };

/** An effect block at a level: damage and the burn's damage × `mult`, the burn per tick, the slow as a fraction. */
export function petHitAt(h: PetHitData, mult: Fix64): PetHit {
  return {
    damage: h.damage.isZero() ? Fix64.Zero : h.damage.mul(mult), damageType: h.damageType,
    burnPerTick: h.burnTicks > 0 ? h.burnPerSecond.mul(mult).divInt(SimConstants.TicksPerSecond) : Fix64.Zero, burnTicks: h.burnTicks,
    slow: Fix64.fromFraction(h.slowPercent, PERCENT), slowTicks: h.slowTicks, stunTicks: h.stunTicks, bossStunTicks: h.bossStunTicks,
    markTicks: h.markTicks, markTakenPercent: h.markTakenPercent, markPointsPercent: h.markPointsPercent, dazzleTicks: h.dazzleTicks,
  };
}

/** The run's copy of `pet` at `level` (even-footing runs: the data's evenFootingLevel). */
export function petRule(balance: BalanceData, pet: PetData, level: number, evenFooting = false): PetRule {
  const l = petLevelClamp(balance, evenFooting ? balance.pets.evenFootingLevel : level);
  const { attack, passive, pulse, signature } = pet.abilities, mult = balance.tables.levelMultAt(l);
  let rule: PetRule = { id: pet.id, level: l, spawnCutPercent: passive.kind === "muster" && l >= passive.fromLevel ? passive.spawnCutPercent : 0 };
  if (l >= attack.fromLevel) {
    rule = attack.kind === "bite" ? { ...rule, bite: { damage: attack.damage.mul(mult), intervalTicks: attack.intervalTicks, reach: attack.reach } }
      : { ...rule, strike: { intervalTicks: attack.intervalTicks, reach: attack.reach, targets: TARGETS[attack.targets], airMultPercent: attack.airMultPercent, hit: { ...petHitAt(attack.hit, mult), damage: attack.damage.mul(mult), damageType: attack.damageType } } };
  }
  if (l >= passive.fromLevel && passive.kind === "aura") {
    const a = passive;
    rule = { ...rule, aura: { reach: a.reach, towerAirPercent: a.towerAirPercent, burningTakenPercent: a.burningTakenPercent, troopGuardPercent: a.troopGuardPercent, healEveryTicks: a.healEveryTicks, healPercent: a.healPercent, killPointsPercent: a.killPointsPercent } };
  }
  if (l >= pulse.fromLevel) {
    const p = pulse;
    rule = p.kind === "rallyBark" ? { ...rule, bark: { everyTicks: p.everyTicks, durationTicks: p.durationTicks, reach: p.reach, damagePercent: p.damagePercent, guardPercent: p.guardPercent } }
      : { ...rule, pulse: { everyTicks: p.everyTicks, reach: p.reach, air: p.air, strongest: p.strongest, troopShieldPercent: p.troopShieldPercent, shieldTicks: p.shieldTicks, troopHealPercent: p.troopHealPercent, hit: petHitAt(p.hit, mult) } };
  }
  if (l >= signature.fromLevel) {
    const s = signature;
    rule = s.kind === "howl" ? { ...rule, howl: { reach: s.reach, stunTicks: s.stunTicks, bossStunTicks: s.bossStunTicks } }
      : { ...rule, signature: { reach: s.reach, air: s.air, strongest: s.strongest, hit: petHitAt(s.hit, mult) } };
  }
  return rule;
}
